import React, { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styles from '../../styles/contacts/ContactsWorkspace.module.css';
import ContactAvatar from './ContactAvatar';
import {
  CalendarIcon,
  CheckIcon,
  CloseIcon,
  DownloadIcon,
  FlagIcon,
  GlobeIcon,
  LinkedInIcon,
  MailIcon,
  NoteIcon,
  PhoneIcon,
  PinIcon,
  SendIcon,
  ShareIcon,
  TrashIcon,
  UndoIcon,
  UsersIcon,
} from './ContactsIcons';
import {
  EDITABLE_FIELDS,
  SOURCE_META,
  canRetractCard,
  changedDraftFields,
  contactActivity,
  contactDisplayName,
  contactDraftFrom,
  contactSource,
  externalHref,
  prettyUrl,
  validateContactDraft,
} from './contactsData';

const ContactMap = lazy(() => import('./ContactMap'));

const NOTES_LIMIT = 10000;
const NOTES_DEBOUNCE_MS = 700;

function QuickAction({ icon, label, href, onClick, disabled, title, external }) {
  if (href && !disabled) {
    return (
      <a
        className={styles.quick}
        href={href}
        onClick={onClick}
        title={title || label}
        {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
      >
        {icon}
        <span>{label}</span>
      </a>
    );
  }
  return (
    <button
      type="button"
      className={styles.quick}
      onClick={onClick}
      disabled={disabled}
      title={title || label}
    >
      {icon}
      <span>{label}</span>
    </button>
  );
}

function ViewFields({ contact, onTrackLink }) {
  const linkedin = externalHref(contact.linkedinURL);
  const website = externalHref(contact.additionalURL);

  const rows = [
    {
      label: 'Email',
      value: contact.email ? (
        <a
          className={styles.fieldLink}
          href={`mailto:${contact.email}`}
          onClick={() => onTrackLink?.('email_click')}
        >
          {contact.email}
        </a>
      ) : null,
    },
    {
      label: 'Phone',
      value: contact.phone ? (
        <a
          className={styles.fieldLink}
          href={`tel:${contact.phone}`}
          onClick={() => onTrackLink?.('phone_click')}
        >
          {contact.phone}
        </a>
      ) : null,
    },
    { label: 'Company', value: contact.company || null },
    { label: 'Role', value: contact.role || null },
    { label: 'Title', value: contact.title || null },
    {
      label: 'LinkedIn',
      value: linkedin ? (
        <a
          className={styles.fieldLink}
          href={linkedin}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => onTrackLink?.('linkedin_click')}
        >
          {prettyUrl(contact.linkedinURL)}
        </a>
      ) : null,
    },
    {
      label: 'Website',
      value: website ? (
        <a
          className={styles.fieldLink}
          href={website}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => onTrackLink?.('website_click')}
        >
          {prettyUrl(contact.additionalURL)}
        </a>
      ) : null,
    },
  ];

  return (
    <dl className={styles.fieldList}>
      {rows.map((row) => (
        <div key={row.label} className={styles.fieldRow}>
          <dt className={styles.fieldLabel}>{row.label}</dt>
          <dd className={styles.fieldValue}>
            {row.value || <span className={styles.fieldEmpty}>Not recorded</span>}
          </dd>
        </div>
      ))}
    </dl>
  );
}

function EditFields({ contact, onCancel, onSave }) {
  const [draft, setDraft] = useState(() => contactDraftFrom(contact));
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const firstFieldRef = useRef(null);

  useEffect(() => {
    firstFieldRef.current?.focus({ preventScroll: true });
  }, []);

  const submit = async (event) => {
    event.preventDefault();
    if (saving) return;
    const nextErrors = validateContactDraft(draft);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    const changes = changedDraftFields(draft, contact);
    if (Object.keys(changes).length === 0) {
      onCancel();
      return;
    }

    setSaving(true);
    try {
      const ok = await onSave(changes);
      if (ok) onCancel();
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} noValidate>
      <div className={styles.editGrid}>
        {EDITABLE_FIELDS.map((field, index) => {
          const errorId = `edit-${field.id}-error`;
          const hasError = Boolean(errors[field.id]);
          return (
            <div
              key={field.id}
              className={`${styles.field} ${field.id === 'name' || field.id === 'email' ? styles.editFull : ''}`}
            >
              <label className={styles.label} htmlFor={`edit-${field.id}`}>
                {field.label}
                {field.hint ? <span className={styles.labelHint}> — {field.hint}</span> : null}
              </label>
              <input
                ref={index === 0 ? firstFieldRef : undefined}
                id={`edit-${field.id}`}
                className={`${styles.input} ${hasError ? styles.inputError : ''}`}
                type={field.type}
                inputMode={field.inputMode}
                autoComplete={field.autoComplete || 'off'}
                value={draft[field.id] || ''}
                onChange={(event) => setDraft((prev) => ({ ...prev, [field.id]: event.target.value }))}
                aria-invalid={hasError || undefined}
                aria-describedby={hasError ? errorId : undefined}
                required={field.required}
              />
              {hasError ? (
                <span className={styles.errorText} id={errorId}>
                  {errors[field.id]}
                </span>
              ) : null}
            </div>
          );
        })}
      </div>

      <div className={styles.formActions}>
        <button type="submit" className={`${styles.btn} ${styles.btnPrimary}`} disabled={saving}>
          {saving ? <span className={styles.spinner} aria-hidden="true" /> : <CheckIcon size={16} />}
          {saving ? 'Saving…' : 'Save changes'}
        </button>
        <button type="button" className={`${styles.btn} ${styles.btnGhost}`} onClick={onCancel} disabled={saving}>
          Cancel
        </button>
      </div>
      <p className={styles.formNote}>
        Edits apply to your own copy of this contact. They do not change the card its owner publishes.
      </p>
    </form>
  );
}

function NotesSection({ contact, onSaveNotes }) {
  const [value, setValue] = useState(contact.notes || '');
  const [status, setStatus] = useState('idle');
  const timerRef = useRef(null);
  const contactIdRef = useRef(contact._id);
  // Edit still inside the debounce window, so it can be rescued if the panel
  // switches contact or closes before the timer fires.
  const pendingRef = useRef(null);
  const saveRef = useRef(onSaveNotes);
  saveRef.current = onSaveNotes;

  const flush = useCallback(
    async (text, contactId) => {
      pendingRef.current = null;
      setStatus('saving');
      try {
        await saveRef.current(contactId, text);
        setStatus('saved');
      } catch {
        setStatus('error');
      }
    },
    []
  );

  /** Commits a pending edit without reporting on it: its contact is gone. */
  const rescuePending = useCallback(() => {
    const pending = pendingRef.current;
    pendingRef.current = null;
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (!pending) return;
    Promise.resolve(saveRef.current(pending.contactId, pending.text)).catch(() => {});
  }, []);

  // Re-seed only when the panel switches contact: background revalidation must
  // not overwrite what the user is in the middle of typing.
  useEffect(() => {
    if (contactIdRef.current === contact._id) return;
    contactIdRef.current = contact._id;
    rescuePending();
    setValue(contact.notes || '');
    setStatus('idle');
  }, [contact._id, contact.notes, rescuePending]);

  // Closing the sheet must not discard an edit either.
  useEffect(() => rescuePending, [rescuePending]);

  const handleChange = (event) => {
    const next = event.target.value;
    setValue(next);
    setStatus('pending');
    if (timerRef.current) clearTimeout(timerRef.current);
    const contactId = contact._id;
    pendingRef.current = { contactId, text: next };
    timerRef.current = setTimeout(() => flush(next, contactId), NOTES_DEBOUNCE_MS);
  };

  const statusLabel = {
    idle: '',
    pending: 'Unsaved changes',
    saving: 'Saving…',
    saved: 'Saved',
    error: 'Could not save — retry',
  }[status];

  return (
    <section className={styles.section}>
      <div className={styles.sectionHead}>
        <h3 className={styles.sectionTitle}>
          <NoteIcon size={14} />
          Private notes
        </h3>
        <span
          className={[
            styles.saveStatus,
            status === 'saved' ? styles.saveStatusSaved : '',
            status === 'error' ? styles.saveStatusError : '',
          ]
            .filter(Boolean)
            .join(' ')}
          role="status"
          aria-live="polite"
        >
          {status === 'saving' ? <span className={styles.spinner} aria-hidden="true" /> : null}
          {status === 'saved' ? <CheckIcon size={13} /> : null}
          {statusLabel}
        </span>
      </div>
      <textarea
        className={styles.textarea}
        value={value}
        onChange={handleChange}
        maxLength={NOTES_LIMIT}
        placeholder={`Where you met ${contactDisplayName(contact)}, what to follow up on…`}
        aria-label={`Private notes about ${contactDisplayName(contact)}`}
      />
      <div className={styles.notesFoot}>
        <span>Saved automatically. Only you can see these notes.</span>
        <span>{value.length ? `${value.length.toLocaleString()} / ${NOTES_LIMIT.toLocaleString()}` : ''}</span>
      </div>
      {status === 'error' ? (
        <div className={styles.formActions}>
          <button
            type="button"
            className={`${styles.btn} ${styles.btnGhost} ${styles.btnSmall}`}
            onClick={() => flush(value, contact._id)}
          >
            Retry save
          </button>
        </div>
      ) : null}
    </section>
  );
}

/**
 * Contact profile. Persistent side panel on desktop, bottom sheet on touch.
 * Every section is driven by fields the Contact schema actually holds, so the
 * panel never advertises data Velox does not have.
 */
export default function ContactDetail({
  contact,
  isDesktop,
  now,
  onClose,
  onToggleFlag,
  onSaveDetails,
  onSaveNotes,
  onDelete,
  onSendCard,
  onRetractCard,
  onSaveVcf,
  onShare,
  onScheduleMeeting,
  onOpenFullMap,
  onTrackLink,
}) {
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    setIsEditing(false);
  }, [contact?._id]);

  const activity = useMemo(() => contactActivity(contact, now), [contact, now]);

  if (!contact) {
    return (
      <div className={styles.detailPlaceholder}>
        <UsersIcon size={26} />
        <p className={styles.stateTitle}>No contact selected</p>
        <p className={styles.stateBody}>
          Pick someone from the list to see their details, notes and where you met.
        </p>
      </div>
    );
  }

  const name = contactDisplayName(contact);
  const source = contactSource(contact);
  const hasLocation = typeof contact.lat === 'number' && typeof contact.lng === 'number';
  const linkedin = externalHref(contact.linkedinURL);
  const website = externalHref(contact.additionalURL);
  const cardAlreadyShared = Boolean(contact.fromInvitation) && !contact.contactDetailsSent;
  const showRetract = canRetractCard(contact);

  return (
    <div className={styles.detail}>
      <div className={styles.detailHead}>
        <ContactAvatar contact={contact} size="lg" />
        <div className={styles.detailHeadText}>
          <h2 className={styles.detailName}>{name}</h2>
          {contact.role || contact.company ? (
            <p className={styles.detailRole}>
              {[contact.role, contact.company].filter(Boolean).join(' · ')}
            </p>
          ) : null}
          <div className={styles.detailBadges}>
            <span className={styles.badge} title={SOURCE_META[source].full}>
              {SOURCE_META[source].label}
            </span>
            {contact.contactDetailsSent ? (
              <span className={`${styles.badge} ${styles.badgeSent}`}>Card sent</span>
            ) : contact.theyHaveMyCard ? (
              <span className={`${styles.badge} ${styles.badgeSent}`}>Has your card</span>
            ) : null}
            {contact.isFlagged ? <span className={`${styles.badge} ${styles.badgeGold}`}>Flagged</span> : null}
          </div>
        </div>
        <div className={styles.detailHeadTools}>
          <button
            type="button"
            className={`${styles.iconBtn} ${contact.isFlagged ? styles.iconBtnOn : ''}`}
            onClick={() => onToggleFlag(contact)}
            aria-pressed={Boolean(contact.isFlagged)}
            aria-label={contact.isFlagged ? 'Remove flag' : 'Flag for follow-up'}
          >
            <FlagIcon filled={Boolean(contact.isFlagged)} />
          </button>
          {!isDesktop ? (
            <button type="button" className={styles.iconBtn} onClick={onClose} aria-label="Close contact">
              <CloseIcon />
            </button>
          ) : null}
        </div>
      </div>

      <div className={styles.quickGrid}>
        <QuickAction
          icon={<MailIcon />}
          label="Email"
          href={contact.email ? `mailto:${contact.email}` : null}
          disabled={!contact.email}
          title={contact.email || 'No email recorded'}
          onClick={() => onTrackLink?.('email_click')}
        />
        <QuickAction
          icon={<PhoneIcon />}
          label="Call"
          href={contact.phone ? `tel:${contact.phone}` : null}
          disabled={!contact.phone}
          title={contact.phone || 'No phone number recorded'}
          onClick={() => onTrackLink?.('phone_click')}
        />
        <QuickAction
          icon={<LinkedInIcon />}
          label="LinkedIn"
          href={linkedin}
          external
          disabled={!linkedin}
          title={linkedin ? prettyUrl(contact.linkedinURL) : 'No LinkedIn recorded'}
          onClick={() => onTrackLink?.('linkedin_click')}
        />
        <QuickAction
          icon={<GlobeIcon />}
          label="Website"
          href={website}
          external
          disabled={!website}
          title={website ? prettyUrl(contact.additionalURL) : 'No website recorded'}
          onClick={() => onTrackLink?.('website_click')}
        />
        <QuickAction icon={<DownloadIcon />} label="Save" title="Save to phone contacts" onClick={() => onSaveVcf(contact)} />
        <QuickAction icon={<ShareIcon />} label="Share" onClick={() => onShare(contact)} />
        {showRetract ? (
          <QuickAction icon={<UndoIcon />} label="Retract" title="Retract my card" onClick={() => onRetractCard(contact)} />
        ) : (
          <QuickAction
            icon={<SendIcon />}
            label="My card"
            title={
              cardAlreadyShared
                ? 'This contact already has your card from the invitation'
                : 'Send my card to this contact'
            }
            disabled={cardAlreadyShared}
            onClick={() => onSendCard(contact)}
          />
        )}
        <QuickAction icon={<CalendarIcon />} label="Meet" title="Request a meeting" onClick={() => onScheduleMeeting(contact)} />
      </div>

      <section className={styles.section}>
        <div className={styles.sectionHead}>
          <h3 className={styles.sectionTitle}>
            <UsersIcon size={14} />
            Details
          </h3>
          {!isEditing ? (
            <button
              type="button"
              className={`${styles.btn} ${styles.btnGhost} ${styles.btnSmall}`}
              onClick={() => setIsEditing(true)}
            >
              Edit
            </button>
          ) : null}
        </div>
        {isEditing ? (
          <EditFields
            contact={contact}
            onCancel={() => setIsEditing(false)}
            onSave={(changes) => onSaveDetails(contact, changes)}
          />
        ) : (
          <ViewFields contact={contact} onTrackLink={onTrackLink} />
        )}
      </section>

      <NotesSection contact={contact} onSaveNotes={onSaveNotes} />

      <section className={styles.section}>
        <div className={styles.sectionHead}>
          <h3 className={styles.sectionTitle}>
            <PinIcon size={14} />
            Where you met
          </h3>
        </div>
        {hasLocation ? (
          <>
            <div className={styles.mapSurface}>
              <Suspense fallback={<div className={styles.mapFallback}>Loading map…</div>}>
                <ContactMap position={[contact.lat, contact.lng]} />
              </Suspense>
            </div>
            <div className={styles.mapCaption}>
              <span>Captured when you first connected.</span>
              <button type="button" className={styles.inlineLink} onClick={onOpenFullMap}>
                Open full map
              </button>
            </div>
          </>
        ) : (
          <div className={styles.mutedRow}>
            <PinIcon size={16} />
            <span>
              No location was captured for this contact. Locations are only recorded when both people allow
              it at the moment of the tap.
            </span>
          </div>
        )}
      </section>

      <section className={styles.section}>
        <div className={styles.sectionHead}>
          <h3 className={styles.sectionTitle}>
            <CalendarIcon size={14} />
            Activity
          </h3>
        </div>
        <ul className={styles.timeline}>
          {activity.map((event) => (
            <li
              key={event.id}
              className={`${styles.timelineItem} ${event.tone === 'primary' ? styles.timelinePrimary : ''}`}
            >
              <p className={styles.timelineTitle}>{event.title}</p>
              {event.meta ? <p className={styles.timelineMeta}>{event.meta}</p> : null}
            </li>
          ))}
        </ul>
      </section>

      <div className={styles.dangerZone}>
        <p className={styles.dangerText}>
          Removing a contact deletes your copy, including your notes. It cannot be undone.
        </p>
        <button type="button" className={`${styles.btn} ${styles.btnDanger} ${styles.btnSmall}`} onClick={() => onDelete(contact)}>
          <TrashIcon size={15} />
          Remove contact
        </button>
      </div>
    </div>
  );
}
